/**
 * Cross-hub conversation listing for `wayai conversations --org <id>` / `--all`.
 *
 * A support-queue check ("which conversations are sitting on `team` right now?")
 * spans every hub an operator can see, not the one hub their workspace happens to
 * be pointed at. This composes the EXISTING per-hub reads — org enumeration
 * (`GET /api/ci/organizations`), hub enumeration (`GET /api/ci/hubs`), and the
 * per-hub `GET /api/conversations` — so authorization stays exactly where it
 * already is: each hub read is independently gated by
 * `loadHub(..., 'conversation:read')`. There is no cross-hub index, no new store,
 * and no server-side fan-out.
 *
 * **What "every hub" means here, exactly.** `GET /api/ci/hubs` enumerates an org's
 * non-branch PREVIEW hubs that the caller's grants cover, and returns each one's
 * `production_hub_id`. Production hubs have no enumeration endpoint of their own,
 * so the target set is those previews PLUS the production parents they name
 * (deduplicated). That discloses nothing new — the parent id is already in the
 * enumeration response — and grants nothing: a parent the caller cannot read fails
 * its own `loadHub`. Because nothing VOUCHED for a derived parent, that refusal is
 * an expected outcome (`hubs_out_of_scope`) rather than a failure, while any hub
 * the caller actually enumerated refusing IS a failure. What this cannot reach is a
 * production hub whose preview the caller's grants do NOT cover, since nothing
 * enumerates it; `resolveTargets` returning EMPTY is therefore a reportable
 * outcome, not an empty queue — see `CrossHubResult.hubs_queried`.
 *
 * Bounds (both axes are stateable before the first request):
 * - Hubs:  refused above `MAX_CROSS_HUB_HUBS`, so a listing is never silently truncated.
 * - Rows:  at most `MAX_CROSS_HUB_HUBS × limit` (limit is capped at 100 by
 *          `listConversationsQuery`), each an already-bounded per-hub page.
 * - Requests: `CROSS_HUB_CONCURRENCY` in flight, and the total stays inside the
 *          150-req/min per-user budget `/api/conversations/*` already carries.
 */

import type {
  CiHubListResponse,
  CiOrganizationsResponse,
  GetConversationsResponse,
} from '@wayai/core/contracts';
import { ApiError, type ConversationListOptions } from './api-client.js';
import { extractApiMessage } from './errors.js';
import { expected } from './expected.js';

/**
 * Refuse rather than truncate above this many hubs. A queue check that silently
 * dropped hubs would report an empty queue that isn't empty — the same failure
 * shape as gh #2452's silent cold listing.
 */
export const MAX_CROSS_HUB_HUBS = 100;

/** In-flight per-hub requests. Well inside `/api/conversations/*`'s 150/min user budget. */
export const CROSS_HUB_CONCURRENCY = 6;

export type CrossHubScope =
  | { mode: 'org'; orgId: string }
  | { mode: 'all' };

type Conversation = GetConversationsResponse['conversations'][number];

export interface CrossHubTarget {
  hub_id: string;
  hub_name: string;
  hub_environment: 'preview' | 'production';
  organization_id: string;
  /** Null when the scope named an org id directly and no org listing was fetched. */
  organization_name: string | null;
}

/**
 * A target plus how it got into the set. `derived` marks a production parent this
 * code inferred from a preview rather than one the caller enumerated — the
 * distinction the partial contract turns on, see `listConversationsAcrossHubs`.
 */
interface ResolvedTarget extends CrossHubTarget {
  derived: boolean;
}

export type CrossHubRow = Conversation & CrossHubTarget;

export interface CrossHubFailure {
  /** `org` — the org's hub enumeration failed, so its hubs are unknown. `hub` — one hub read failed. */
  scope_kind: 'org' | 'hub';
  hub_id: string | null;
  hub_name: string | null;
  organization_id: string;
  error: string;
}

/**
 * The scope accounting, stated ONCE.
 *
 * `hubs_queried` mixes three populations — answered, refused-as-derived, failed —
 * so every consumer that re-derives its own number from it gets a different answer
 * and they contradict each other on screen. The derived figures live here instead,
 * and `partial`, the exit code and every rendered count read straight off them.
 * `assertScopeAccounting` pins the partition, so a fourth population cannot be
 * added without the suite failing.
 */
export interface CrossHubResult {
  scope: CrossHubScope;
  organizations_queried: number;
  /**
   * Hubs ATTEMPTED. Partitions exactly:
   * `hubs_queried === hubs_read + hubs_out_of_scope.length + (hub-kind errors)`.
   */
  hubs_queried: number;
  /** Hubs that actually answered. The only count that describes real coverage. */
  hubs_read: number;
  /**
   * Derived production parents the caller's grants do not cover, by hub id. NOT
   * failures: nothing enumerated them, and the help text already says a production
   * hub outside the caller's enumerable set needs `--hub <id>`. Kept out of
   * `errors` so `partial` keeps meaning "a hub in your scope did not answer".
   */
  hubs_out_of_scope: string[];
  /**
   * Scopes the caller actually asked for: attempted hubs minus the out-of-scope
   * ones, plus each org whose enumeration failed (its hubs never became targets).
   * The denominator of "N of M could not be read" — never `hubs_queried`, which
   * counts hubs this very run reported as deliberately unread.
   */
  scopes_total: number;
  /** Of `scopes_total`, how many did not answer. `partial` is exactly `> 0`. */
  scopes_failed: number;
  conversations: CrossHubRow[];
  /** True when at least one org or in-scope hub read failed — the listing is incomplete. */
  partial: boolean;
  errors: CrossHubFailure[];
}

/** The slice of `ApiClient` this module needs — keeps it unit-testable. */
export interface CrossHubClient {
  organizations(): Promise<CiOrganizationsResponse>;
  listHubs(organizationId: string): Promise<CiHubListResponse>;
  getConversations(hubId: string, opts?: ConversationListOptions): Promise<GetConversationsResponse>;
}

/** Run `worker` over `items` with at most `concurrency` in flight, preserving input order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Enumerate the hubs in scope, recording an org whose enumeration failed rather
 * than quietly dropping its hubs from the count.
 */
async function resolveTargets(
  client: CrossHubClient,
  orgs: Array<{ id: string; name: string | null }>,
  errors: CrossHubFailure[],
): Promise<ResolvedTarget[]> {
  const perOrg = await mapWithConcurrency(orgs, CROSS_HUB_CONCURRENCY, async (org) => {
    try {
      const { hubs } = await client.listHubs(org.id);
      const targets: ResolvedTarget[] = [];
      for (const hub of hubs) {
        targets.push({
          hub_id: hub.hub_id,
          hub_name: hub.hub_name,
          hub_environment: 'preview',
          organization_id: org.id,
          organization_name: org.name,
          derived: false,
        });
        // The production parent carries the real queue; nothing enumerates it
        // directly, and its id is already in this response. Its own `loadHub`
        // still decides whether this caller may read it — and because nothing
        // vouched for it, a refusal there is expected rather than a failure.
        if (hub.production_hub_id) {
          targets.push({
            hub_id: hub.production_hub_id,
            hub_name: hub.hub_name,
            hub_environment: 'production',
            organization_id: org.id,
            organization_name: org.name,
            derived: true,
          });
        }
      }
      return targets;
    } catch (err) {
      errors.push({
        scope_kind: 'org',
        hub_id: null,
        hub_name: null,
        organization_id: org.id,
        error: extractApiMessage(err),
      });
      return [];
    }
  });

  // Several previews of one hub name the same production parent — read it once.
  const seen = new Set<string>();
  return perOrg.flat().filter((target) => {
    if (seen.has(target.hub_id)) return false;
    seen.add(target.hub_id);
    return true;
  });
}

/**
 * List conversations across every hub in scope.
 *
 * Never throws on a per-hub failure: the hub is recorded in `errors` and the
 * result is marked `partial`, so a caller can distinguish "the queue is empty"
 * from "some hubs did not answer". Throws only for the hub cap, which is a
 * refusal to produce a silently-truncated answer.
 *
 * **`partial` means a hub in your scope did not answer — nothing else.** A DERIVED
 * production parent refused by AUTHORIZATION is not that: nothing enumerated it,
 * and a token scoped `environments: ['preview']` would otherwise be permanently
 * `partial` with a non-zero exit on every run, which is exactly the state a
 * monitoring script must be able to tell an outage apart from. Those land in
 * `hubs_out_of_scope`. Any OTHER failure on a derived parent — a 500, a timeout —
 * is a real failure and stays in `errors`: only the refusal is expected.
 */
export async function listConversationsAcrossHubs(
  client: CrossHubClient,
  scope: CrossHubScope,
  opts: ConversationListOptions,
): Promise<CrossHubResult> {
  const errors: CrossHubFailure[] = [];

  const orgs: Array<{ id: string; name: string | null }> = scope.mode === 'all'
    ? (await client.organizations()).organizations
    // A named org is not enumerated, so its display name is genuinely unknown —
    // carrying the uuid in the name slot would make `--org` and `--all` emit
    // different `organization_name` values for the very same rows.
    : [{ id: scope.orgId, name: null }];

  const targets = await resolveTargets(client, orgs, errors);
  if (targets.length > MAX_CROSS_HUB_HUBS) {
    throw expected(
      `${targets.length} hubs are in scope, above the ${MAX_CROSS_HUB_HUBS}-hub cap for one ` +
        'cross-hub listing. Narrow the scope with --org <organization_id>.',
    );
  }

  const outOfScope: string[] = [];
  let hubsRead = 0;

  const perHub = await mapWithConcurrency(targets, CROSS_HUB_CONCURRENCY, async (target) => {
    const { derived, ...row } = target;
    try {
      const result = await client.getConversations(target.hub_id, opts);
      hubsRead++;
      return result.conversations.map((conv) => ({ ...conv, ...row }));
    } catch (err) {
      if (derived && isAuthorizationRefusal(err)) {
        outOfScope.push(target.hub_id);
        return [];
      }
      errors.push({
        scope_kind: 'hub',
        hub_id: target.hub_id,
        hub_name: target.hub_name,
        organization_id: target.organization_id,
        error: extractApiMessage(err),
      });
      return [];
    }
  });

  // Newest activity first across every hub — the order a queue is worked in, on
  // the SAME axis each hub ranked its own page by: `updated_at` is the response's
  // `last_message_at` and is null until the first message, so falling back to
  // `created_at` mirrors HubDO's `LAST_ACTIVITY_SQL`. Without the fallback a
  // never-answered conversation ranks first under `--hub` and last under `--all`,
  // burying the newest thing in the queue in the very view built to surface it.
  // Keyed once per row rather than per comparison (the set is bounded at
  // MAX_CROSS_HUB_HUBS x the contract's 100-row page); `created_at` is
  // non-nullable, so every row gets a real key and no NaN comparison arises.
  const conversations = perHub
    .flat()
    .map((row) => ({ row, key: Date.parse(row.updated_at ?? row.created_at) }))
    .sort((a, b) => b.key - a.key)
    .map(({ row }) => row);

  // An org whose enumeration failed contributes no targets, so its scope is not in
  // `hubs_queried` and has to be added; the out-of-scope hubs ARE in it and have to
  // come out. Every rendered count reads off these two — the renderer does no
  // arithmetic of its own, which is what kept the on-screen numbers disagreeing.
  const scopesTotal = targets.length - outOfScope.length
    + errors.filter((f) => f.scope_kind === 'org').length;

  const result: CrossHubResult = {
    scope,
    organizations_queried: orgs.length,
    hubs_queried: targets.length,
    hubs_read: hubsRead,
    hubs_out_of_scope: outOfScope,
    scopes_total: scopesTotal,
    scopes_failed: errors.length,
    conversations,
    partial: errors.length > 0,
    errors,
  };
  assertScopeAccounting(result);
  return result;
}

/**
 * Did this request fail because the caller's grants do not cover the hub?
 *
 * Narrow on purpose: only a 403 means "not yours". A 404, a 500, or a network
 * failure on the same hub is a real failure and must keep marking the listing
 * partial — widening this predicate would let an outage read as "out of scope".
 */
function isAuthorizationRefusal(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/**
 * Every attempted hub lands in exactly one bucket, and `partial` agrees with
 * `scopes_failed`. Two review rounds in a row produced a count that contradicted
 * another count on the same screen; this turns the next one into a thrown error at
 * the seam that owns the accounting rather than a plausible wrong number on an
 * operator's terminal.
 */
function assertScopeAccounting(result: CrossHubResult): void {
  const hubFailures = result.errors.filter((f) => f.scope_kind === 'hub').length;
  const accountedFor = result.hubs_read + result.hubs_out_of_scope.length + hubFailures;
  if (accountedFor !== result.hubs_queried) {
    throw new Error(
      `cross-hub accounting: ${result.hubs_queried} hubs attempted but ${accountedFor} accounted for ` +
        `(read ${result.hubs_read}, out of scope ${result.hubs_out_of_scope.length}, failed ${hubFailures})`,
    );
  }
  if (result.partial !== result.scopes_failed > 0) {
    throw new Error(`cross-hub accounting: partial=${result.partial} disagrees with scopes_failed=${result.scopes_failed}`);
  }
}
